/**
 * ============================================================
 * Style Playground — Auto-generated metadata
 * ============================================================
 * Device: desktop
 * Language: English
 *
 * ## Style Direction (from analysis)
 * <style-name>StudioBrut</style-name>
 * **Mode:** Light
 * 
 * **Style Recipe:**
 * - **Core Visual Style:** Pop-Brutalism (Playful Neo-Brutalism)
 * - **Layout Structure:** Bento Box / Modular
 * - **Visual Complexity:** Balanced
 * 
 * **Implementation Techniques:**
 * - **Material & Surfaces:** Flat, opaque color blocks. No gradients, no blur. High tactile feel through chunky geometry.
 * - **Border-Radius:** Mixed. Sharp 0px corners for major layout boundaries, mixed with perfectly round pill-shapes for tags, badges, and buttons.
 * - **Typography:** Bold, aggressive typography. A highly geometric or slightly quirky display font (e.g., Space Grotesk, Syne) for headers, paired with a highly readable monospace for all component metrics and tokens.
 * - **Lighting & Shadows:** Hard, unblurred offset shadows (e.g., `box-shadow: 4px 4px 0px #000000`). Buttons physically depress on click (translating X and Y to cover the shadow).
 * - **Border Treatments:** Thick, stark 2px solid black (`#111111`) borders around every distinct component, input, and card.
 * 
 * **Color Direction:**
 * A warm Cream/Beige (`#FDFBF7`) acts as the base canvas. Accents use high-saturation, retro-pop colors: Acid Yellow, Electric Blue, and Hot Pink. These are used for status tags (e.g., Pink for 'Deprecated', Yellow for 'Draft', Blue for 'Published') and hover states. This provides a highly creative, agency-like aesthetic that breaks the monotony of standard enterprise tooling.
 * ============================================================
 */

import React, { useState } from 'react';
import { Icon } from '@iconify/react';

export default function StylePreview() {
  const [activeTab, setActiveTab] = useState('Design');
  const [activeNav, setActiveNav] = useState('components');

  const tabs = ['Design', 'Code', 'Props', 'Guidelines'];

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'lucide:layout-dashboard' },
    { id: 'components', label: 'Components', icon: 'lucide:component' },
    { id: 'tokens', label: 'Design Tokens', icon: 'lucide:palette' },
    { id: 'changelog', label: 'Changelog', icon: 'lucide:history' },
  ];

  const propsData = [
    { name: 'variant', type: "'primary' | 'secondary' | 'danger'", default: "'primary'", desc: 'Defines the visual style of the button.' },
    { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", desc: 'Size of the button.' },
    { name: 'disabled', type: 'boolean', default: 'false', desc: 'If true, the button is disabled and cannot be interacted with.' },
    { name: 'onClick', type: '() => void', default: '-', desc: 'Handler called when the button is clicked.' },
  ];

  return (
    <div className="flex h-screen w-full bg-[#FDFBF7] text-black font-sans selection:bg-[#FF00FF] selection:text-white overflow-hidden">
      
      {/* Sidebar */}
      <aside className="w-64 border-r-2 border-black flex flex-col bg-[#FDFBF7] z-10">
        <div className="p-6 border-b-2 border-black flex items-center gap-3">
          <div className="w-8 h-8 bg-[#00E5FF] border-2 border-black rounded-full shadow-[2px_2px_0px_0px_#000000]"></div>
          <span className="font-black text-xl tracking-tight uppercase">CompHub</span>
        </div>
        
        <nav className="flex-1 p-4 flex flex-col gap-3 overflow-y-auto">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveNav(item.id)}
              className={`flex items-center gap-3 px-4 py-3 border-2 border-black text-left font-bold transition-all duration-150
                ${activeNav === item.id 
                  ? 'bg-[#FFE81A] shadow-[4px_4px_0px_0px_#000000] -translate-y-1' 
                  : 'bg-white hover:bg-gray-50 hover:shadow-[2px_2px_0px_0px_#000000] hover:-translate-y-0.5'
                }`}
            >
              <Icon icon={item.icon} className="text-xl" />
              {item.label}
            </button>
          ))}
        </nav>
        
        <div className="p-4 border-t-2 border-black">
          <button className="w-full flex items-center gap-3 px-4 py-3 bg-white border-2 border-black font-bold hover:bg-gray-50 hover:shadow-[4px_4px_0px_0px_#000000] transition-all hover:-translate-y-1">
            <Icon icon="lucide:settings" className="text-xl" />
            Settings
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden relative">
        {/* Background Grid Pattern (Subtle Brutalist Touch) */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, black 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>

        {/* Header */}
        <header className="p-6 border-b-2 border-black bg-[#FDFBF7] flex items-center justify-between z-10 relative">
          <div className="flex items-center gap-4">
            <button className="p-2 bg-white border-2 border-black hover:bg-[#FF00FF] hover:text-white transition-colors shadow-[2px_2px_0px_0px_#000000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
              <Icon icon="lucide:arrow-left" className="text-xl" />
            </button>
            <h1 className="text-3xl font-black uppercase tracking-tight">Primary Button</h1>
            <div className="flex gap-2 ml-4">
              <span className="px-3 py-1 bg-[#00E5FF] border-2 border-black font-bold text-sm rounded-full shadow-[2px_2px_0px_0px_#000000]">Published</span>
              <span className="px-3 py-1 bg-white border-2 border-black font-mono font-bold text-sm rounded-full shadow-[2px_2px_0px_0px_#000000]">v1.2.0</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 bg-white border-2 border-black font-bold flex items-center gap-2 hover:bg-[#FFE81A] transition-all shadow-[4px_4px_0px_0px_#000000] hover:-translate-y-1 active:translate-x-[4px] active:translate-y-[4px] active:shadow-none">
              <Icon icon="lucide:history" />
              Versions
            </button>
            <button className="px-4 py-2 bg-black text-white border-2 border-black font-bold flex items-center gap-2 hover:bg-gray-800 transition-all shadow-[4px_4px_0px_0px_#00E5FF] hover:-translate-y-1 active:translate-x-[4px] active:translate-y-[4px] active:shadow-none">
              <Icon icon="lucide:play" />
              Preview
            </button>
            <button className="p-2 bg-white border-2 border-black hover:bg-gray-100 transition-all shadow-[2px_2px_0px_0px_#000000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
              <Icon icon="lucide:more-vertical" className="text-xl" />
            </button>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex border-b-2 border-black bg-[#FDFBF7] px-6 pt-4 gap-2 z-10 relative">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-black text-lg border-2 border-black border-b-0 uppercase transition-all
                ${activeTab === tab 
                  ? 'bg-[#FF00FF] text-white -translate-y-1 shadow-[4px_-4px_0px_0px_rgba(0,0,0,1)] z-20' 
                  : 'bg-white hover:bg-gray-100 text-black z-10 mt-1'
                }
              `}
              style={{
                marginBottom: activeTab === tab ? '-2px' : '0'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-auto p-8 relative z-0">
          
          {activeTab === 'Design' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white border-2 border-black shadow-[8px_8px_0px_0px_#000000] p-4 flex flex-col gap-4">
                <div className="flex items-center justify-between border-b-2 border-black pb-4">
                  <h2 className="font-black text-xl uppercase">Design Specs</h2>
                  <button className="px-3 py-1 border-2 border-black bg-[#FFE81A] font-bold text-sm shadow-[2px_2px_0px_0px_#000000]">Download Asset</button>
                </div>
                <div className="aspect-video bg-[#FDFBF7] border-2 border-dashed border-black flex items-center justify-center p-12">
                  <div className="flex gap-8 items-center justify-center">
                    <button className="px-6 py-3 bg-[#00E5FF] border-2 border-black shadow-[4px_4px_0px_0px_#000000] font-bold text-lg rounded-full">Primary Button</button>
                    <button className="px-6 py-3 bg-[#FF00FF] text-white border-2 border-black shadow-[4px_4px_0px_0px_#000000] font-bold text-lg rounded-full">Hover State</button>
                    <button className="px-6 py-3 bg-gray-300 border-2 border-black shadow-[4px_4px_0px_0px_#000000] font-bold text-lg rounded-full opacity-50 cursor-not-allowed">Disabled</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Props' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white border-2 border-black shadow-[8px_8px_0px_0px_#000000] overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#FFE81A] border-b-2 border-black">
                      <th className="p-4 border-r-2 border-black font-black uppercase text-sm w-1/5">Name</th>
                      <th className="p-4 border-r-2 border-black font-black uppercase text-sm w-1/4">Type</th>
                      <th className="p-4 border-r-2 border-black font-black uppercase text-sm w-1/6">Default</th>
                      <th className="p-4 font-black uppercase text-sm">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propsData.map((prop, idx) => (
                      <tr key={idx} className="border-b-2 border-black last:border-b-0 hover:bg-gray-50 transition-colors">
                        <td className="p-4 border-r-2 border-black font-bold">{prop.name}</td>
                        <td className="p-4 border-r-2 border-black font-mono text-sm text-[#FF00FF]">{prop.type}</td>
                        <td className="p-4 border-r-2 border-black font-mono text-sm bg-gray-100">{prop.default}</td>
                        <td className="p-4 text-gray-800">{prop.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'Code' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-[#111] text-white border-2 border-black shadow-[8px_8px_0px_0px_#FFE81A] p-6 rounded-none relative group">
                <button className="absolute top-4 right-4 p-2 bg-white text-black border-2 border-black font-bold shadow-[2px_2px_0px_0px_#000000] opacity-0 group-hover:opacity-100 transition-opacity active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                  <Icon icon="lucide:copy" />
                </button>
                <pre className="font-mono text-sm leading-relaxed overflow-x-auto">
                  <code className="text-[#00E5FF]">import</code> {'{ '}Button{' }'} <code className="text-[#00E5FF]">from</code> <span className="text-[#FFE81A]">'@/components/ui/button'</span>;{'\n\n'}
                  <code className="text-[#00E5FF]">export default function</code> App() {'{\n'}
                  {'  '}<code className="text-[#00E5FF]">return</code> {'(\n'}
                  {'    '}<span className="text-[#FF00FF]">{'<'}Button</span> <span className="text-[#FFE81A]">variant</span>=<span className="text-[#FFE81A]">"primary"</span> <span className="text-[#FFE81A]">size</span>=<span className="text-[#FFE81A]">"lg"</span><span className="text-[#FF00FF]">{'>'}</span>{'\n'}
                  {'      '}Click Me{'\n'}
                  {'    '}<span className="text-[#FF00FF]">{'</'}Button{'>'}</span>{'\n'}
                  {'  );\n'}
                  {'}'}
                </pre>
              </div>
            </div>
          )}

          {activeTab === 'Guidelines' && (
            <div className="max-w-3xl mx-auto">
              <div className="bg-white border-2 border-black shadow-[8px_8px_0px_0px_#000000] p-8 prose prose-lg max-w-none">
                <h2 className="font-black text-3xl uppercase mb-6 border-b-4 border-[#FF00FF] inline-block pb-1">Usage Guidelines</h2>
                <p className="font-bold text-xl leading-relaxed mb-6">
                  The Primary Button is the main call to action on a page. There should ideally be only one primary button visible at a time to maintain clear hierarchy.
                </p>
                <div className="flex gap-4 mb-8">
                  <div className="flex-1 border-2 border-black p-4 bg-[#FDFBF7] relative">
                    <span className="absolute -top-3 left-4 bg-[#00E5FF] border-2 border-black px-2 py-0.5 font-black text-sm uppercase shadow-[2px_2px_0px_0px_#000000]">Do</span>
                    <ul className="list-disc pl-5 mt-2 font-bold">
                      <li>Use for the primary action.</li>
                      <li>Keep labels short and action-oriented.</li>
                    </ul>
                  </div>
                  <div className="flex-1 border-2 border-black p-4 bg-[#FDFBF7] relative">
                    <span className="absolute -top-3 left-4 bg-[#FF00FF] text-white border-2 border-black px-2 py-0.5 font-black text-sm uppercase shadow-[2px_2px_0px_0px_#000000]">Don't</span>
                    <ul className="list-disc pl-5 mt-2 font-bold">
                      <li>Use multiple primary buttons.</li>
                      <li>Use vague labels like "Submit".</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
